const libPictApplication = require('pict-application');

// Provider
const libDocumentationProvider = require('./providers/Pict-Provider-Docuserve-Documentation.js');
const libDemosProvider         = require('./providers/Pict-Provider-Docuserve-Demos.js');

// Shared modal + theme stack
const libPictSectionModal = require('pict-section-modal');
const libPictSectionTheme = require('pict-section-theme');

// Precomputed brand (icon, name, palette, colors).  Generated by
// `npm run brand` from Retold-Modules-Manifest.json + this module's
// `retold.brand` package.json block.
const libDocuserveBrand = require('./Docuserve-Brand.js');

// Views
const libViewLayout       = require('./views/PictView-Docuserve-Layout.js');
const libViewTopBarNav    = require('./views/PictView-Docuserve-TopBar-Nav.js');
const libViewTopBarUser   = require('./views/PictView-Docuserve-TopBar-User.js');
const libViewSidebar      = require('./views/PictView-Docuserve-Sidebar.js');
const libViewSplash       = require('./views/PictView-Docuserve-Splash.js');
const libViewContent      = require('./views/PictView-Docuserve-Content.js');
const libViewSearch       = require('./views/PictView-Docuserve-Search.js');
const libViewDemo         = require('./views/PictView-Docuserve-Demo.js');
const libViewFablePlayground = require('./views/PictView-Docuserve-Fable-Playground.js');
const libViewSectionPlayground = require('./views/PictView-Docuserve-Section-Playground.js');

class DocuserveApplication extends libPictApplication
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		// Add the documentation provider
		this.pict.addProvider('Docuserve-Documentation', libDocumentationProvider.default_configuration, libDocumentationProvider);

		// Add the demos registry provider.  Libraries that ship demos
		// (e.g. pict-section-form, pict-section-code) call
		// `pict.providers['Docuserve-Demos'].registerAll([...])` at
		// app boot to populate it.
		this.pict.addProvider('Docuserve-Demos', libDemosProvider.default_configuration, libDemosProvider);

		// Modal section — drives the shell() + addPanel() layout machinery,
		// plus dialogs / tooltips / toasts.
		this.pict.addView('Pict-Section-Modal', libPictSectionModal.default_configuration, libPictSectionModal);

		// Add views — the layout, sidebar, splash, content, search are
		// docuserve's own content surfaces.  The two TopBar-* slot views
		// fill the Theme-TopBar's Nav and User slots.
		this.pict.addView('Docuserve-Layout',       libViewLayout.default_configuration,       libViewLayout);
		this.pict.addView('Docuserve-TopBar-Nav',   libViewTopBarNav.default_configuration,    libViewTopBarNav);
		this.pict.addView('Docuserve-TopBar-User',  libViewTopBarUser.default_configuration,   libViewTopBarUser);
		this.pict.addView('Docuserve-Sidebar',      libViewSidebar.default_configuration,      libViewSidebar);
		this.pict.addView('Docuserve-Splash',       libViewSplash.default_configuration,       libViewSplash);
		this.pict.addView('Docuserve-Content',      libViewContent.default_configuration,      libViewContent);
		this.pict.addView('Docuserve-Search',       libViewSearch.default_configuration,       libViewSearch);
		this.pict.addView('Docuserve-Demo',         libViewDemo.default_configuration,         libViewDemo);
		this.pict.addView('Docuserve-Fable-Playground', libViewFablePlayground.default_configuration, libViewFablePlayground);
		this.pict.addView('Docuserve-Section-Playground', libViewSectionPlayground.default_configuration, libViewSectionPlayground);

		// Theme-Section is added later from onAfterInitializeAsync, after
		// the brand has been resolved.  Docuserve is a viewer — the docs
		// being served dictate the brand, not docuserve itself.  See
		// `_resolveBrand()` for the resolution order.
		this._docuserveBrandOverride = (pOptions && pOptions.Brand) ? pOptions.Brand : null;
	}

	/**
	 * Resolution order for the brand applied to Theme-Section:
	 *
	 *   1. options.Brand passed to the constructor (host override).
	 *   2. _brand.json fetched from the docs root.  Written there by the
	 *      `pict-docuserve inject` CLI from the calling module's
	 *      `package.json#retold.brand` block — so when docuserve serves
	 *      module X's docs, the docs wear module X's brand.
	 *   3. Docuserve's own precomputed brand (libDocuserveBrand) as the
	 *      last-resort fallback so the chrome is never blank.
	 *
	 * Invoked synchronously-or-asynchronously by onAfterInitializeAsync;
	 * `fCallback(pBrand)` always fires exactly once.
	 */
	_resolveBrand(fCallback)
	{
		if (this._docuserveBrandOverride)
		{
			return fCallback(this._docuserveBrandOverride);
		}

		// _brand.json lives next to index.html — same origin, relative URL.
		// In a server-side / non-browser context (tests, prerender) fetch
		// isn't available, so we fall back to the bundled default
		// immediately.
		if (typeof fetch !== 'function')
		{
			return fCallback(libDocuserveBrand);
		}

		fetch('_brand.json').then((pResponse) =>
		{
			if (!pResponse || !pResponse.ok)
			{
				return null;
			}
			return pResponse.json();
		}).then((pBrand) =>
		{
			if (pBrand && (pBrand.Name || pBrand.Hash || pBrand.Colors))
			{
				return fCallback(pBrand);
			}
			fCallback(libDocuserveBrand);
		}).catch(() =>
		{
			fCallback(libDocuserveBrand);
		});
	}

	_addThemeSection(pBrand)
	{
		this.pict.addProvider('Theme-Section',
		{
			ApplyDefault: 'retold-default',
			DefaultMode:  'system',
			DefaultScale: 1.0,
			Brand:        pBrand,
			Views: ['Picker', 'ModeToggle', 'ScaleSelect', 'Button', 'BrandMark', 'TopBar'],
			ViewOptions:
			{
				// Height matches the topbar panel Size in the Layout view.
				TopBar: { NavView: 'Docuserve-TopBar-Nav', UserView: 'Docuserve-TopBar-User', Height: 56 }
			}
		}, libPictSectionTheme);
	}

	onAfterInitializeAsync(fCallback)
	{
		// Initialize application state
		this.pict.AppData.Docuserve =
		{
			CatalogLoaded: false,
			Catalog: null,
			CoverLoaded: false,
			Cover: null,
			SidebarLoaded: false,
			SidebarGroups: [],
			TopBarLoaded: false,
			TopBar: null,
			VersionLoaded: false,
			Version: null,
			ErrorPageLoaded: false,
			ErrorPageHTML: null,
			KeywordIndexLoaded: false,
			KeywordDocumentCount: 0,
			CurrentGroup: '',
			CurrentModule: '',
			CurrentPath: '',
			// Whether the sidebar is currently visible
			SidebarVisible: true,
			// Base URL for local docs (relative to where the app is served)
			DocsBaseURL: '',
			// URL for the catalog JSON
			CatalogURL: 'retold-catalog.json',
			// Derived record arrays consumed by the sidebar's {~TS:~}
			// tags.  Initialised empty so the first render emits no rows
			// rather than tripping on undefined addresses.
			SidebarGroupRecords:    [],
			SidebarSearchSlot:      [],
			SidebarSearchResults:   [],
			SidebarSearchOverflow:  [],
			SidebarSearchEmpty:     [],
			ModuleNavSections:      [],
			FooterSlot:             [],
			// Search-page state consumed by the search view's {~TS:~}/{~D:~} tags.
			SearchResults:          [],
			SearchStatus:           ''
		};

		// Resolve the brand (host override / _brand.json / bundled default)
		// then wire Theme-Section.  Theme-Section must exist before
		// Docuserve-Layout renders because the Layout panel pulls
		// Theme-TopBar as its top ContentView.
		this._resolveBrand((tmpBrand) =>
		{
			this._addThemeSection(tmpBrand);
			this._loadCatalogAndRender(fCallback);
		});
	}

	_loadCatalogAndRender(fCallback)
	{
		// Load the catalog, then render the layout
		let tmpDocProvider = this.pict.providers['Docuserve-Documentation'];
		tmpDocProvider.loadCatalog(() =>
		{
			// Set the page title to match the stamped <title> pattern
			// "<DisplayName> v<Version> Documentation" so the browser
			// tab and the static HTML metadata stay in sync.  Falls back
			// to the cover/topbar brand alone when no version is loaded.
			let tmpDocuserve = this.pict.AppData.Docuserve;
			let tmpDisplayName = '';
			if (tmpDocuserve.CoverLoaded && tmpDocuserve.Cover && tmpDocuserve.Cover.Title)
			{
				tmpDisplayName = tmpDocuserve.Cover.Title.replace(/<[^>]*>/g, '').trim();
			}
			else if (tmpDocuserve.TopBarLoaded && tmpDocuserve.TopBar && tmpDocuserve.TopBar.Brand)
			{
				tmpDisplayName = tmpDocuserve.TopBar.Brand.replace(/<[^>]*>/g, '').trim();
			}
			if (tmpDisplayName)
			{
				if (tmpDocuserve.VersionLoaded && tmpDocuserve.Version && tmpDocuserve.Version.Version)
				{
					document.title = `${tmpDisplayName} v${tmpDocuserve.Version.Version} Documentation`;
				}
				else
				{
					document.title = tmpDisplayName;
				}
			}

			// Render the layout shell, which triggers child view rendering
			this.pict.views['Docuserve-Layout'].render();

			return super.onAfterInitializeAsync(fCallback);
		});
	}

	/**
	 * Read the current window.location.hash and dispatch to the correct handler.
	 *
	 * Route patterns:
	 *   #/Home                         -> showView('Docuserve-Splash')
	 *   #/search/<query>               -> navigateToSearch(query)
	 *   #/page/<docpath>               -> navigateToPage(docpath)
	 *   #/doc/<group>/<module>          -> navigateToModule(group, module)
	 *   #/doc/<group>/<module>/<path>   -> navigateToModulePath(group, module, path)
	 */
	resolveHash()
	{
		this._resolveHashRoute();
		// Keep the topbar "view source" (octocat) link pointed at whatever
		// module the current route resolves to (fixed on per-module sites,
		// changes per navigation on the aggregate).
		let tmpTopBarUser = this.pict.views['Docuserve-TopBar-User'];
		if (tmpTopBarUser && (typeof tmpTopBarUser.render === 'function'))
		{
			tmpTopBarUser.render();
		}
	}

	_resolveHashRoute()
	{
		let tmpHash = (window.location.hash || '').replace(/^#\/?/, '');

		if (!tmpHash || tmpHash === 'Home')
		{
			this.showView('Docuserve-Splash');
			return;
		}

		let tmpParts = tmpHash.split('/');

		if (tmpParts[0] === 'search')
		{
			let tmpQuery = decodeURIComponent(tmpParts.slice(1).join('/'));
			this.navigateToSearch(tmpQuery);
			return;
		}

		if (tmpParts[0] === 'page' && tmpParts.length >= 2)
		{
			// Rejoin everything after 'page/' in case the path has slashes
			let tmpDocPath = tmpParts.slice(1).join('/');
			this.navigateToPage(tmpDocPath);
			return;
		}

		if (tmpParts[0] === 'doc' && tmpParts.length >= 3)
		{
			let tmpGroup = tmpParts[1];
			let tmpModule = tmpParts[2];
			if (tmpParts.length >= 4)
			{
				let tmpPath = tmpParts.slice(3).join('/');
				this.navigateToModulePath(tmpGroup, tmpModule, tmpPath);
			}
			else
			{
				this.navigateToModule(tmpGroup, tmpModule);
			}
			return;
		}

		// #/demo/<group>/<module>/<hash> — interactive demo registered
		// with Docuserve-Demos.  Mounts the demo's view into the content
		// area + renders any Sources tabs alongside.
		if (tmpParts[0] === 'demo' && tmpParts.length >= 4)
		{
			let tmpGroup = tmpParts[1];
			let tmpModule = tmpParts[2];
			let tmpHash = tmpParts.slice(3).join('/');
			this.navigateToDemo(tmpGroup, tmpModule, tmpHash);
			return;
		}

		// #/playground/<kind> — live editor + sandbox.  Three shapes:
		//   #/playground/fable                       — Fable JS REPL (bottom drawer)
		//   #/playground/section                     — section playground for the
		//                                              current module (standalone-mode
		//                                              docs site)
		//   #/playground/section/<group>/<module>    — section playground for a
		//                                              specific module (catalog-mode
		//                                              docs site serving multiple)
		if (tmpParts[0] === 'playground' && tmpParts.length >= 2)
		{
			if (tmpParts[1] === 'section')
			{
				let tmpGroup  = (tmpParts.length >= 4) ? tmpParts[2] : (this.pict.AppData.Docuserve.CurrentGroup || '');
				let tmpModule = (tmpParts.length >= 4) ? tmpParts[3] : (this.pict.AppData.Docuserve.CurrentModule || '');
				this.navigateToSectionPlayground(tmpGroup, tmpModule);
				return;
			}
			this.navigateToPlayground(tmpParts[1]);
			return;
		}

		// Unknown route — treat as a page
		this.navigateToPage(tmpHash);
	}

	/**
	 * Navigate to an interactive demo registered with Docuserve-Demos.
	 *
	 * @param {string} pGroup  - catalog group key
	 * @param {string} pModule - module name
	 * @param {string} pHash   - demo hash (unique within group/module)
	 */
	navigateToDemo(pGroup, pModule, pHash)
	{
		let tmpDemoView  = this.pict.views['Docuserve-Demo'];
		let tmpSidebarView = this.pict.views['Docuserve-Sidebar'];

		this.pict.AppData.Docuserve.CurrentGroup  = pGroup;
		this.pict.AppData.Docuserve.CurrentModule = pModule;
		this.pict.AppData.Docuserve.CurrentDemo   = pHash;
		this.pict.AppData.Docuserve.CurrentPath   = '';

		if (tmpDemoView && typeof tmpDemoView.showDemo === 'function')
		{
			tmpDemoView.showDemo(pGroup, pModule, pHash);
		}

		// Keep the sidebar in sync — show module-scoped demos + nav.
		if (tmpSidebarView)
		{
			tmpSidebarView.renderSidebarGroups();
			if (typeof tmpSidebarView.renderModuleNav === 'function')
			{
				tmpSidebarView.renderModuleNav(pGroup, pModule);
			}
		}
		this._syncPlaygroundVisibility();
	}

	/**
	 * Open a playground.  The playground itself lives in a persistent
	 * shell drawer at the bottom of the viewport (see Layout view) —
	 * this method navigates the main content area to the matching
	 * explainer doc page AND slides the drawer up.
	 *
	 * @param {string} pKind - Playground identifier ('fable', 'pict', ...)
	 */
	navigateToPlayground(pKind)
	{
		let tmpLayout = this.pict.views['Docuserve-Layout'];

		if (pKind === 'fable')
		{
			// Send the user to the explainer page so a deep-link to
			// #/playground/fable lands somewhere readable, then force
			// the drawer visible (overriding the per-module opt-in
			// suppression — explicit user intent wins) and expand it.
			this.navigateToPage('fable-playground');
			if (tmpLayout && typeof tmpLayout.setPlaygroundEnabled === 'function')
			{
				tmpLayout.setPlaygroundEnabled(true);
			}
			if (tmpLayout && typeof tmpLayout.expandPlayground === 'function')
			{
				tmpLayout.expandPlayground();
			}
			return;
		}

		// Unknown playground kind — fall back to a friendly notice in the
		// content area rather than silently dropping the navigation.
		let tmpContentView = this.pict.views['Docuserve-Content'];
		if (tmpContentView && typeof tmpContentView.displayContent === 'function')
		{
			tmpContentView.render();
			tmpContentView.displayContent('<h1>Playground not available</h1><p>No playground is registered for <code>' + pKind + '</code>.</p>');
		}
	}

	/**
	 * Open the section playground for `<group>/<module>`.  Unlike the
	 * Fable playground (which lives in the bottom drawer), the section
	 * playground takes over the main content area — it needs full
	 * vertical space for its four editor tabs + iframe pane.
	 *
	 * @param {string} pGroup
	 * @param {string} pModule
	 */
	navigateToSectionPlayground(pGroup, pModule)
	{
		this.pict.AppData.Docuserve.CurrentGroup  = pGroup;
		this.pict.AppData.Docuserve.CurrentModule = pModule;
		this.pict.AppData.Docuserve.CurrentDemo   = '';
		this.pict.AppData.Docuserve.CurrentPath   = 'section-playground';

		// Hide the bottom playground drawer — the section playground
		// is the whole show on this route.
		let tmpLayout = this.pict.views['Docuserve-Layout'];
		if (tmpLayout && typeof tmpLayout.setPlaygroundEnabled === 'function')
		{
			tmpLayout.setPlaygroundEnabled(false);
		}

		let tmpPlayground = this.pict.views['Docuserve-Section-Playground'];
		if (!tmpPlayground || typeof tmpPlayground.openPlayground !== 'function')
		{
			let tmpContentView = this.pict.views['Docuserve-Content'];
			if (tmpContentView && typeof tmpContentView.displayContent === 'function')
			{
				tmpContentView.render();
				tmpContentView.displayContent('<h1>Section playground unavailable</h1><p>The Section Playground view is not registered.</p>');
			}
			return;
		}
		tmpPlayground.openPlayground(pGroup, pModule);

		// Keep the sidebar in sync — show the module's nav + active item.
		let tmpSidebarView = this.pict.views['Docuserve-Sidebar'];
		if (tmpSidebarView)
		{
			tmpSidebarView.renderSidebarGroups();
			if (typeof tmpSidebarView.renderModuleNav === 'function')
			{
				tmpSidebarView.renderModuleNav(pGroup, pModule);
			}
		}
	}

	/**
	 * Navigate to a hash route.
	 *
	 * Sets window.location.hash, which triggers the hashchange listener in the
	 * layout view, which calls resolveHash() automatically.
	 *
	 * @param {string} pRoute - The route path (e.g. '/Home', '/page/quick-start')
	 */
	navigateTo(pRoute)
	{
		window.location.hash = pRoute;
	}

	/**
	 * Show a specific view in the content area.
	 *
	 * @param {string} pViewIdentifier - The view identifier to render
	 */
	showView(pViewIdentifier)
	{
		if (pViewIdentifier in this.pict.views)
		{
			this.pict.AppData.Docuserve.CurrentGroup = '';
			this.pict.AppData.Docuserve.CurrentModule = '';
			this.pict.AppData.Docuserve.CurrentPath = '';

			this.pict.views[pViewIdentifier].render();

			// Update sidebar to clear module nav and refresh active states
			this.pict.views['Docuserve-Sidebar'].clearModuleNav();
			this.pict.views['Docuserve-Sidebar'].renderSidebarGroups();
		}
		this._syncPlaygroundVisibility();
	}

	/**
	 * Toggle the bottom playground drawer's visibility based on whether
	 * the current group/module opts in (via a "Code Playground" entry in
	 * its _sidebar.md / catalog Sidebar).  Invoked at the tail of every
	 * navigation method so the panel doesn't reserve space on modules
	 * that haven't opted in.
	 */
	_syncPlaygroundVisibility()
	{
		let tmpLayout = this.pict.views['Docuserve-Layout'];
		let tmpDoc    = this.pict.providers['Docuserve-Documentation'];
		if (!tmpLayout || typeof tmpLayout.setPlaygroundEnabled !== 'function') { return; }
		let tmpGroup  = this.pict.AppData.Docuserve.CurrentGroup;
		let tmpModule = this.pict.AppData.Docuserve.CurrentModule;
		let tmpEnabled = (tmpDoc && typeof tmpDoc.isPlaygroundEnabled === 'function')
			? tmpDoc.isPlaygroundEnabled(tmpGroup, tmpModule)
			: false;
		tmpLayout.setPlaygroundEnabled(tmpEnabled);
		if (typeof tmpLayout.setPlaygroundTitle === 'function')
		{
			tmpLayout.setPlaygroundTitle(tmpModule
				? ('JS Playground: ' + tmpModule)
				: 'JS Playground');
		}
		// Load the module's _playground.json.  Two reasons to always do
		// this (regardless of tmpEnabled):
		//   1. The async `Kind` check inside isPlaygroundEnabled needs
		//      the cache populated.  If the config declares Kind:
		//      "section", we have to re-call setPlaygroundEnabled(false)
		//      to hide the Fable drawer that the sync sidebar-based
		//      check just turned on.
		//   2. Apps still consult AppData.Docuserve.Playground.Config
		//      (DOM-sandbox pane visibility etc.).
		if (tmpDoc && typeof tmpDoc.loadPlaygroundConfig === 'function')
		{
			tmpDoc.loadPlaygroundConfig(tmpGroup, tmpModule).then((pConfig) =>
			{
				if (!this.pict.AppData.Docuserve.Playground)
				{
					this.pict.AppData.Docuserve.Playground = {};
				}
				this.pict.AppData.Docuserve.Playground.Config = pConfig || null;

				// Re-evaluate the drawer's enabled state now that the
				// cache is populated — a Kind: "section" config flips
				// it from true back to false.
				if (typeof tmpDoc.isPlaygroundEnabled === 'function')
				{
					let tmpReEnabled = tmpDoc.isPlaygroundEnabled(tmpGroup, tmpModule);
					tmpLayout.setPlaygroundEnabled(tmpReEnabled);
				}

				// Config controls DOM-sandbox pane visibility; let the
				// playground view re-evaluate the moment it lands.  No-op
				// if the view hasn't been mounted yet.
				let tmpPlaygroundView = this.pict.views['Docuserve-Fable-Playground'];
				if (tmpPlaygroundView && typeof tmpPlaygroundView._applySandboxMode === 'function')
				{
					tmpPlaygroundView._applySandboxMode();
				}
			});
		}
	}

	/**
	 * Navigate to a module's documentation.
	 *
	 * @param {string} pGroup - The group key
	 * @param {string} pModule - The module name
	 */
	navigateToModule(pGroup, pModule)
	{
		this.navigateToModulePath(pGroup, pModule, 'README.md');
	}

	/**
	 * Navigate to a specific path within a module's documentation.
	 *
	 * @param {string} pGroup - The group key
	 * @param {string} pModule - The module name
	 * @param {string} pPath - The document path
	 */
	navigateToModulePath(pGroup, pModule, pPath)
	{
		let tmpDocProvider = this.pict.providers['Docuserve-Documentation'];
		let tmpContentView = this.pict.views['Docuserve-Content'];
		let tmpSidebarView = this.pict.views['Docuserve-Sidebar'];

		// Update current navigation state
		this.pict.AppData.Docuserve.CurrentGroup = pGroup;
		this.pict.AppData.Docuserve.CurrentModule = pModule;
		this.pict.AppData.Docuserve.CurrentPath = pPath;

		// Render the content view shell and show loading
		tmpContentView.render();
		tmpContentView.showLoading();

		// Update sidebar to show active module and module-specific nav
		tmpSidebarView.renderSidebarGroups();
		tmpSidebarView.renderModuleNav(pGroup, pModule);
		this._syncPlaygroundVisibility();

		// Resolve the document URL and fetch it
		let tmpURL = tmpDocProvider.resolveDocumentURL(pGroup, pModule, pPath || 'README.md');

		if (!tmpURL)
		{
			tmpContentView.displayContent(tmpDocProvider.getErrorPageHTML(pGroup + '/' + pModule));
			return;
		}

		let tmpDocPath = pPath || 'README.md';
		tmpDocProvider.fetchDocument(tmpURL, (pError, pHTML) =>
		{
			if (!pError)
			{
				tmpContentView.displayContent(pHTML);
				return;
			}

			// Remote fetch failed — try a local fallback using the
			// group/module/path as a relative path.  This handles cases
			// where the catalog contains entries (e.g. example apps)
			// that don't correspond to real GitHub repositories.
			let tmpLocalPath = pGroup + '/' + pModule + '/' + tmpDocPath;
			tmpDocProvider.fetchLocalDocument(tmpLocalPath, (pLocalError, pLocalHTML) =>
			{
				tmpContentView.displayContent(pLocalHTML);
			}, pGroup, pModule, tmpDocPath);
		}, pGroup, pModule, tmpDocPath);
	}

	/**
	 * Navigate to a local documentation page (e.g. architecture.md).
	 *
	 * @param {string} pDocPath - The doc path without extension
	 */
	navigateToPage(pDocPath)
	{
		let tmpDocProvider = this.pict.providers['Docuserve-Documentation'];
		let tmpContentView = this.pict.views['Docuserve-Content'];
		let tmpSidebarView = this.pict.views['Docuserve-Sidebar'];

		// Update state
		this.pict.AppData.Docuserve.CurrentGroup = '';
		this.pict.AppData.Docuserve.CurrentModule = '';
		this.pict.AppData.Docuserve.CurrentPath = pDocPath;

		// Render the content view shell and show loading
		tmpContentView.render();
		tmpContentView.showLoading();

		// Clear module-specific sidebar nav
		tmpSidebarView.clearModuleNav();
		tmpSidebarView.renderSidebarGroups();
		this._syncPlaygroundVisibility();

		// Fetch the local document
		let tmpPath = pDocPath;
		if (!tmpPath.match(/\.md$/))
		{
			tmpPath = tmpPath + '.md';
		}

		tmpDocProvider.fetchLocalDocument(tmpPath, (pError, pHTML) =>
		{
			// On a miss the path may be a directory reference — retry
			// <path>/README.md so directory-style links resolve.
			if (pError && !tmpPath.match(/(^|\/)README\.md$/i))
			{
				let tmpReadmePath = tmpPath.replace(/\.md$/i, '') + '/README.md';
				tmpDocProvider.fetchLocalDocument(tmpReadmePath, (pReadmeError, pReadmeHTML) =>
				{
					// Show the README on success, else the original
					// not-found page (it names the path the user asked for).
					tmpContentView.displayContent(pReadmeError ? pHTML : pReadmeHTML);
				}, '', '', tmpReadmePath);
				return;
			}
			// fetchDocument always provides displayable HTML in pHTML,
			// even on error, so we can use it directly.
			tmpContentView.displayContent(pHTML);
		}, '', '', tmpPath);
	}

	/**
	 * Navigate to the search page with an optional query.
	 *
	 * @param {string} pQuery - The search query (may be empty for blank search page)
	 */
	navigateToSearch(pQuery)
	{
		let tmpSidebarView = this.pict.views['Docuserve-Sidebar'];
		let tmpSearchView = this.pict.views['Docuserve-Search'];

		// Update state
		this.pict.AppData.Docuserve.CurrentGroup = '';
		this.pict.AppData.Docuserve.CurrentModule = '';
		this.pict.AppData.Docuserve.CurrentPath = '';

		// Clear module-specific sidebar nav
		tmpSidebarView.clearModuleNav();
		tmpSidebarView.renderSidebarGroups();

		// Render the search view with the query
		tmpSearchView.render();
		tmpSearchView.showSearch(pQuery || '');
		this._syncPlaygroundVisibility();
	}
}

module.exports = DocuserveApplication;

module.exports.default_configuration = require('./Pict-Application-Docuserve-Configuration.json');
